import { useGetPromotionalCreditAppPlan } from '@src/api/compute-api'
import { formatCurrency, formatCurrencyToNearestUpperMultipleToNoDecimals } from '@if/core/utils'
import { FilteredCreditApplication } from '@src/api/credit-api'
import BackButton from '@src/components/BackButton'
import ManageCoappButton from '@src/components/ManageCoappButton'
import RemoveCoappButton from '@src/components/RemoveCoappButton'
import { applicantFullName, getLoanAmount, hasSignedContract } from '@src/data/credit-application-selectors'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useTermPaymentsTable from '../../InitialApprobationPage/hooks'

interface Props {
  creditApp: FilteredCreditApplication
}

const CreditSummaryPage = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const creditApplicationId = creditApp.id
  const [monthlyPaymentPerMonths, isLoadingTerms] = useTermPaymentsTable(creditApp, {
    requestedAmount: getLoanAmount(creditApp),
  })
  const [promotionalAmounts] = useGetPromotionalCreditAppPlan(
    getLoanAmount(creditApp),
    creditApplicationId,
    creditApp.merchantPaymentPlanId,
  )
  const minTermPayment = useMemo(
    () => (monthlyPaymentPerMonths ? monthlyPaymentPerMonths[monthlyPaymentPerMonths.length - 1] : null),
    [monthlyPaymentPerMonths],
  )
  const maxTermPayment = useMemo(
    () => (monthlyPaymentPerMonths ? monthlyPaymentPerMonths[0] : null),
    [monthlyPaymentPerMonths],
  )

  const hasSignedCvt = hasSignedContract(creditApp)

  if (isLoadingTerms) return null
  return (
    <main className="form-section">
      <div className="credit-steps">
        <div className="applicationInfo-card paragraph">
          <h3>{t('common.appNumber', { number: creditApp.referenceNumber })}</h3>
          <div>
            <p className="applicationInfo-card-subtitle">
              <strong>{t('common.applicant')} : </strong>
              {applicantFullName(creditApp.applicant)}
            </p>
            <p className="applicationInfo-card-subtitle">
              <strong>{t('common.coapplicant')} : </strong>
              {creditApp.coapplicant !== null ? applicantFullName(creditApp.coapplicant) : t('common.none')}
            </p>
          </div>
          {!hasSignedCvt && (
            <div className="btn-group space-between">
              <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={creditApp.coapplicant !== null} />
              {creditApp.coapplicant !== null && creditApp.applicant.hasCreditReport && (
                <RemoveCoappButton creditAppId={creditApp.id} />
              )}
            </div>
          )}
        </div>
        <div className="applicationInfo-card paragraph">
          <h3>{t('loan.details')}</h3>
          <div>
            <p>
              <strong>{t('common.maxLoanAmount')} : </strong>
              {formatCurrency(creditApp.finalDecision.maxAmountFinanced)}
            </p>
            <p>
              <strong>{t('common.requestedAmount')} : </strong>
              {formatCurrency(creditApp.worksheet?.amountRequested)}
            </p>
            {creditApp.merchantPaymentPlan === null && minTermPayment && maxTermPayment && (
              <>
                <p>
                  <strong>{t('common.monthlyPaymentMin')} : </strong>
                  {t('loan.monthPeriodItem', {
                    loanTerm: minTermPayment.term,
                    loanAmount: formatCurrency(minTermPayment.amountWithoutInsurance),
                  })}
                </p>
                <p>
                  <strong>{t('common.monthlyPaymentMax')} : </strong>
                  {t('loan.monthPeriodItem', {
                    loanTerm: maxTermPayment.term,
                    loanAmount: formatCurrency(maxTermPayment.amountWithoutInsurance),
                  })}
                </p>
                <p>
                  {t('common.monthlyAmountsInclude', {
                    financeFee: creditApp?.normsDecision.lenderFeeRate,
                    interestRate: creditApp?.finalDecision.interestRate,
                  })}
                </p>
              </>
            )}
            {creditApp.merchantPaymentPlan !== null && promotionalAmounts && (
              <>
                <p>
                  <strong>{t('common.monthlyPayment')} : </strong>
                  {t('loan.monthPeriodItem', {
                    loanTerm: creditApp.merchantPaymentPlan.loanTerm,
                    loanAmount: formatCurrencyToNearestUpperMultipleToNoDecimals(
                      promotionalAmounts.paymentForFrequency,
                      5,
                    ),
                  })}
                </p>
                {creditApp.merchantPaymentPlan.borrowerFeeRate > 0 && (
                  <p>{t('decision.includedFee', { adminFeeRate: creditApp.merchantPaymentPlan.borrowerFeeRate })}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <BackButton />
    </main>
  )
}

export default CreditSummaryPage
